import { SingleSpeakerSelect } from '@src/components/content-components/SingleSpeakerSelect';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { LogManager } from '@src/utility/Logger';
import {
  buildSpeakersFromMappings,
  extractSpeakerLabelsFromTranscriptElements,
  fetchDatoSpeakers,
  fetchSpeakerVoiceMappings,
} from '@src/utility/speakerUtils';
import { useEffect, useState } from 'react';

import type {
  ControlRenderProps,
  SpeakerControlSetting,
} from '../ControlTypes';
import type { Speaker } from '@src/components/content-components/SpeakerTypes';

export const SpeakerControl = ({
  control,
  onSpeakerControlChange,
  unifiedValue,
}: ControlRenderProps<SpeakerControlSetting>) => {
  const logger = LogManager.getLogger('component:SpeakerControl');
  const videoCreator = useVideoCreatorStore();
  const [speakers, setSpeakers] = useState<Speaker[]>([]);
  const [selectedSpeaker, setSelectedSpeaker] = useState<Speaker | null>(null);
  const [isLoadingSpeakers, setIsLoadingSpeakers] = useState(false);

  useEffect(() => {
    const storyId = videoCreator.story?.id;
    if (!storyId) return;
    let cancelled = false;
    const loadSpeakers = async () => {
      setIsLoadingSpeakers(true);
      try {
        const transcriptElements = videoCreator.rootStore?.transcriptionStore
          ?.getFinalTranscriptionElements?.()
          ?.filter(el => el.state !== 'removed' && el.state !== 'cut');
        const labels =
          extractSpeakerLabelsFromTranscriptElements(transcriptElements);
        if (!labels.length) {
          if (!cancelled) {
            setSpeakers([]);
            setSelectedSpeaker(null);
          }
          return;
        }
        const [speakerMappings, datoSpeakers] = await Promise.all([
          fetchSpeakerVoiceMappings(storyId),
          fetchDatoSpeakers(storyId, videoCreator.datoClientStore),
        ]);
        if (cancelled) return;
        const built = buildSpeakersFromMappings(
          labels,
          speakerMappings,
          datoSpeakers,
        );
        setSpeakers(built);
      } catch (error) {
        logger.warn('Failed to load speakers', { error });
      } finally {
        if (!cancelled) setIsLoadingSpeakers(false);
      }
    };
    loadSpeakers();

    return () => {
      cancelled = true;
    };
  }, [
    videoCreator.story?.id,
    videoCreator.datoClientStore,
    videoCreator.rootStore,
  ]);

  useEffect(() => {
    // If unifiedValue changes from outside, update selectedSpeaker accordingly
    if (typeof unifiedValue === 'string') {
      const match = speakers.find(s => s.speakerLabel === unifiedValue);
      setSelectedSpeaker(match || null);
    } else {
      setSelectedSpeaker(null);
    }
  }, [unifiedValue, speakers]);

  const handleSpeakerChange = (next: Speaker[]) => {
    const first = next[0] ?? null;
    if (
      first &&
      (!selectedSpeaker ||
        selectedSpeaker.speakerLabel !== first.speakerLabel) &&
      typeof onSpeakerControlChange === 'function' &&
      typeof control.controlBindings !== 'undefined'
    ) {
      setSelectedSpeaker(first);
      onSpeakerControlChange(control.controlBindings, first);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 500 }}>{control.label}</div>
      {isLoadingSpeakers ? (
        <div style={{ fontSize: 12, opacity: 0.7 }}>Loading speakers…</div>
      ) : speakers.length === 0 ? (
        <div style={{ fontSize: 12, opacity: 0.7 }}>No speakers detected.</div>
      ) : (
        <SingleSpeakerSelect
          speakers={speakers}
          selectedSpeakers={selectedSpeaker ? [selectedSpeaker] : []}
          onChange={handleSpeakerChange}
        />
      )}
    </div>
  );
};
